import { Box, Button, Col, Collapse, NewDateInput, Icon, Row, TextInput, themes, Loader } from '@digitools/honest-react';
import React, { useEffect, useState } from 'react';
import useTranslate from 'src/hooks/useTranslate';
import { RIA_TEXT } from '../constants/RiaConstants';
import useRiaConfig from '../hooks/useRiaConfig';
import useRiaData from '../hooks/useRiaData';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import useTealium from 'src/hooks/useTealium';
import { absenceConfigDateValidation, validateDateRange, validateDuration } from '../utils/validationUtils';
import { useArrayField, useFormApi } from 'informed';
import { RIA_VALIDATION_CONSTANTS } from '../constants/RiaValidationConstants';
import { RedBulletList } from 'src/components/Formatting/StyledComponents';
import { StyledCollapseHeading } from './LayoutComponents';
import styled from 'styled-components';
import { addDays, max, min, subDays } from 'date-fns';
const RemoveIcon = styled(Icon) `
  font-size: 1.5rem;
  color: ${({ theme }) => theme.palette.secondaryDark} !important;
  margin-top: .6rem;
  @media (max-width: 575px) {
    position: relative;
    bottom: 2rem;
  }
`;
const AddButton = styled(Button) `
  color: ${({ theme }) => theme.palette.secondaryDark} !important;
  margin-top: 0;
  padding: 1rem 1rem;
  cursor: pointer;
`;
const RemoveButton = styled(Button) `
  padding-left: 1rem;
  padding-top: 0.75rem;
  font-size: 1rem;
  color: ${({ theme }) => theme.palette.secondaryDark} !important;
`;
const AbsenceDuration = () => {
    const { t } = useTranslate();
    const { riaConfigData, riaConfigLoading } = useRiaConfig();
    const { currentLeave, oldAbsences } = useRiaData();
    const formApi = useFormApi();
    const { add, fields } = useArrayField({ field: 'absenceDurations' });
    const [customizedReportingPolicyItem, setCustomizedReportingPolicyItem] = useState();
    const { trackEvent } = useTealium();
    useEffect(() => {
        let reportingPolicy;
        if (riaConfigData?.reportingPolicyItems && riaConfigData?.reportingPolicyItems?.length > 0) {
            reportingPolicy = riaConfigData.reportingPolicyItems[0];
        }
        if (riaConfigData?.customizeReportingPolicy && currentLeave?.ssdCde) {
            const customizedReportingPolicyItems = riaConfigData?.reportingPolicyItems?.filter((reportingPolicyItem) => {
                return reportingPolicyItem.subsidiary === currentLeave?.ssdCde;
            });
            if (!customizedReportingPolicyItems || customizedReportingPolicyItems.length == 0 ||
                customizedReportingPolicyItems[0].numberOfEditingDaysFuture === null ||
                customizedReportingPolicyItems[0].numberOfEditingDaysPast === null ||
                customizedReportingPolicyItems[0].numberOfReportingDaysFuture === null ||
                customizedReportingPolicyItems[0].numberOfReportingDaysPast === null) {
                //FIXME: ?? why are we throwing an uncaught runtime error here? is this supposed to show some sort of Alert?? 
                // Test data was invalidly configured and led to this popping up in tests that previously weren't running correctly
                // there were no subsidiaries on the test leaves so nothing matched in the filter above, making this always null
                // Don't yet see any test for this invalid config situation?
                throw new Error();
            }
            if (customizedReportingPolicyItems && customizedReportingPolicyItems?.length > 0) {
                reportingPolicy = customizedReportingPolicyItems[0];
            }
        }
        if (reportingPolicy != undefined) {
            setCustomizedReportingPolicyItem(reportingPolicy);
        }
        if (fields.length == 0) {
            add();
        }
    }, [currentLeave]);
    let durationInterval = 1;
    if (customizedReportingPolicyItem?.durationInterval !== undefined && customizedReportingPolicyItem?.durationInterval !== null) {
        if (customizedReportingPolicyItem?.durationInterval === 0) {
            durationInterval = 60;
        }
        else {
            durationInterval = customizedReportingPolicyItem?.durationInterval;
        }
    }
    const absenceRangeDateValidation = (value) => {
        if (value && currentLeave) {
            return validateDateRange(value, customizedReportingPolicyItem?.numberOfReportingDaysPast, customizedReportingPolicyItem?.numberOfReportingDaysFuture, currentLeave?.lveBeginDte, currentLeave?.lveEndDte);
        }
    };
    const absenceDateValidation = (value, currentLeave) => {
        let error = absenceConfigDateValidation(value, customizedReportingPolicyItem, false, currentLeave);
        if (!error) {
            error = absenceRangeDateValidation(value);
        }
        return error;
    };
    const isDuplicateDate = (value, inputIndex) => {
        const newAbsences = formApi.getValue('absenceDurations');
        let error = undefined;
        newAbsences?.forEach((newAbsence, index) => {
            if (value && value.length > 0 && inputIndex !== index && newAbsence.absenceDate === value) {
                error = t(RIA_VALIDATION_CONSTANTS.duplicateDateError);
            }
        });
        oldAbsences.forEach((oldAbsence, j) => {
            if (value && value.length > 0 && oldAbsence.absenceDate === value) {
                error = t(RIA_VALIDATION_CONSTANTS.duplicateDateError);
            }
        });
        return error;
    };
    //FIXME: move this to the constants file as a function that returns the result translatable
    const reportingPolicyInstructions = t({
        english: `Absences must be reported up to ${customizedReportingPolicyItem?.numberOfReportingDaysPast} days in the past and up to ${customizedReportingPolicyItem?.numberOfReportingDaysFuture} days in the future.`,
        spanish: `Las ausencias deben informarse hasta ${customizedReportingPolicyItem?.numberOfReportingDaysPast} días en el pasado y hasta ${customizedReportingPolicyItem?.numberOfReportingDaysFuture} días en el futuro.`,
    });
    const formatDuration = (value) => {
        if (value[1] === ':') {
            return [/\d/, /:/, /[0-5]/, /[0-9]/];
        }
        else {
            return [/\d/, /\d/, ':', /[0-5]/, /[0-9]/];
        }
    };
    const handleDurationBlur = () => {
        const value = formApi.getValue('absenceDuration');
        const regex = /:0$/g;
        formApi.setValue('absenceDuration', value?.replace(regex, ':00'));
    };
    const handleOnClick = () => {
        add();
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_name: t(RIA_TEXT.addNewAdditionalAbsence),
            event_type: TEALIUM_EVENT.EVENT_TYPE.REPORT_INTERMITTENT_ABSENCE,
            event_version: 'add or edit existing absences',
        });
    };
    const AbsencesHeading = () => <StyledCollapseHeading>{t(RIA_TEXT.addAbsencesHeader)}</StyledCollapseHeading>;
    return (<Loader isShown={riaConfigLoading}>
      <Box>
        <Collapse title={'Add new absences'} // For ADA
     titleComponent={AbsencesHeading} headerElemType={'h3'} type='alternate' isOpen={true} theme={themes.rebrand} headerLeftDisplay={() => (<Icon aria-hidden={true} style={{ fontSize: '2rem' }} name='view-request-status' className='mr-3' color='primary'/>)} color='accent1'>
          <RedBulletList role='list' aria-label='List of criteria for submitting a new absence'>
            <li className={`mb-3`}>
              <span>{t(RIA_TEXT.leaveDetailsConfirmation)}</span>
            </li>
            <li className={`mb-3`}>
              <span>{reportingPolicyInstructions}</span>
            </li>
            <li className={`mt-3 mb-3`}>
              <span>{t(RIA_TEXT.dateValidationHelpText)}</span>
            </li>
            <li className={`mt-3 mb-3`}>
              <span>{customizedReportingPolicyItem?.durationInterval === 0 ? t(RIA_TEXT.durationHourHelpText) : t(RIA_TEXT.durationValidationHelpText)(durationInterval)}</span>
            </li>
          </RedBulletList>

          {fields.map(({ field, key, remove }, i) => (<Row key={key}>
              <Col lg={4} md={5} sm={6} size={fields.length > 1 ? 11 : 12}>
                <NewDateInput label={t(RIA_TEXT.dateLabel)} data-testid={field + 'absenceDate'} field={field + 'absenceDate'} placeholder={t(RIA_TEXT.dateLabel)} theme={themes.rebrand} validate={(value, values) => {
                // Only required once the paired duration has been filled in - an
                // untouched blank "add new absence" row must stay optional so
                // edit-only submissions of previous absences aren't blocked.
                if (!value && values?.absenceDurations?.[i]?.absenceDuration) {
                    return t(RIA_VALIDATION_CONSTANTS.requiredDateMessage);
                }
                return isDuplicateDate(value, i) || absenceDateValidation(value, currentLeave);
            }} minDate={currentLeave &&
                max([
                    subDays(new Date(), customizedReportingPolicyItem?.numberOfReportingDaysPast),
                    new Date(currentLeave?.lveBeginDte)
                ])} maxDate={currentLeave &&
                min([
                    addDays(new Date(), customizedReportingPolicyItem?.numberOfReportingDaysFuture),
                    new Date(currentLeave?.lveEndDte)
                ])}/>
              </Col>
              <Col lg={4} md={5} sm={6} size={fields.length > 1 ? 11 : 12}>
                <TextInput data-testid={field + 'absenceDuration'} field={field + 'absenceDuration'} label={t(RIA_TEXT.durationLabel)} placeholder={t(RIA_TEXT.durationLabel)} 
        //@ts-ignore FIXME: typing error here
        formatter={formatDuration} validateOnBlur={true} onBlur={handleDurationBlur} validate={(value, values) => validateDuration(value, values?.absenceDurations, i, durationInterval, t)}/>
              </Col>
              {fields.length > 1 && (<>
                  <RemoveIcon data-testid='delete-absence-icon' id={'delete-absence-' + i} title={'Delete entry'} onClick={remove} name={'close'}/>
                  <RemoveButton data-testid='delete-absence-button' onClick={remove} theme={themes.rebrand}>
                      {t(RIA_TEXT.removeAbsence)}
                  </RemoveButton>
                </>)}
              <br />
            </Row>))}
          {fields.length < 5 && (<Row>
              <Col lg={4} md={5} sm={6} size={12}>
                <Icon style={{ marginTop: 'auto', marginBottom: 'auto' }} name={'add-something'} color={'secondaryDark'} data-testid='add-absence-icon' size='md'/> 
                <AddButton buttonType='text' type='button' onClick={handleOnClick} data-testid='add-absence-text' theme={themes.rebrand}>
                    {t(RIA_TEXT.addNewAdditionalAbsence)}
                </AddButton>
              </Col>
            </Row>)}
        </Collapse>
      </Box>
    </Loader>);
};
export default AbsenceDuration;
